import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from './api.service';

@Component({
  selector: 'app-admin-work-orders',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      <div class="max-w-[1500px] mx-auto p-5 space-y-5">
        <header class="flex items-start justify-between gap-4">
          <div>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">Work Orders</h1>
            <p class="text-slate-500 text-xs mt-1">Property work instructions, contacts, tenants, and PDF issue packs.</p>
          </div>
          <a routerLink="/admin/financials/work-orders/new" class="h-9 px-3 rounded-lg bg-[#0C3A5A] text-white text-xs font-bold hover:bg-[#082d46] flex items-center gap-2">
            <i class="ti ti-plus text-[16px]"></i>New Work Order
          </a>
        </header>

        <section class="grid grid-cols-1 md:grid-cols-4 gap-3">
          @for (card of summaryCards(); track card.label) {
            <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3 shadow-sm">
              <p class="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{{ card.label }}</p>
              <p class="text-xl font-semibold text-slate-950 mt-1">{{ card.value }}</p>
              <p class="text-[11px] text-slate-500 mt-1">{{ card.help }}</p>
            </div>
          }
        </section>

        <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
            <div class="relative flex-1 min-w-[260px] max-w-md">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[16px]"></i>
              <input [(ngModel)]="filters.search" (input)="loadWorkOrders()" type="text" placeholder="Search work order, reference, recipient or property"
                class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:border-[#1A6FA6]">
            </div>
            <select [(ngModel)]="filters.status" (change)="loadWorkOrders()" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600">
              <option value="">All status</option>
              @for (status of statuses; track status) {
                <option [value]="status">{{ status }}</option>
              }
            </select>
            <select [(ngModel)]="filters.priority" (change)="loadWorkOrders()" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600">
              <option value="">All priority</option>
              @for (priority of priorities; track priority) {
                <option [value]="priority">{{ priority }}</option>
              }
            </select>
          </div>

          <div class="overflow-x-auto min-h-[420px]">
            <table class="w-full text-left text-sm">
              <thead>
                <tr class="bg-slate-50/80 border-b border-slate-100">
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Work Order</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">To</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Property</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest">Due</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">Priority</th>
                  <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                @for (wo of workOrders(); track wo.id) {
                  <tr [routerLink]="['/admin/financials/work-orders', wo.id]" class="hover:bg-slate-50 cursor-pointer transition-colors">
                    <td class="px-4 py-3">
                      <span class="font-mono text-xs font-semibold px-2 py-1 rounded-md bg-[#EAF5FA] text-[#1A6FA6] border border-[#D5EBF6]">{{ wo.work_order_no }}</span>
                      <p class="text-[10px] text-slate-400 mt-1">Reference {{ wo.reference_no || '-' }}</p>
                    </td>
                    <td class="px-4 py-3">
                      <p class="font-semibold text-slate-900 text-sm">{{ wo.to_name }}</p>
                      <p class="text-[10px] text-slate-400">{{ wo.created_by_name || 'System' }}</p>
                    </td>
                    <td class="px-4 py-3">
                      <p class="text-xs text-slate-700 font-semibold">{{ wo.property_name || '-' }}</p>
                      <p class="text-[10px] text-slate-400 max-w-[260px] truncate">{{ wo.property_address || wo.description || '-' }}</p>
                    </td>
                    <td class="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                      <p>{{ wo.work_due_date ? (wo.work_due_date | date:'dd MMM yyyy') : 'As agreed' }}</p>
                      <p class="text-[10px] text-slate-400">Created {{ wo.created_date | date:'dd MMM yyyy' }}</p>
                    </td>
                    <td class="px-4 py-3 text-center">
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase border" [ngClass]="priorityBadgeClass(wo.priority)">{{ wo.priority }}</span>
                    </td>
                    <td class="px-4 py-3 text-center">
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase border" [ngClass]="statusBadgeClass(wo.status)">{{ wo.status }}</span>
                    </td>
                  </tr>
                }
                @if (!isLoading() && workOrders().length === 0) {
                  <tr>
                    <td colspan="6" class="py-16 text-center text-slate-400">
                      <i class="ti ti-clipboard-list text-4xl mb-3 opacity-25"></i>
                      <p class="font-semibold text-sm">No work orders found.</p>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  `
})
export class AdminWorkOrdersComponent implements OnInit {
  private api = inject(ApiService);
  workOrders = signal<any[]>([]);
  isLoading = signal(false);
  filters: any = { search: '', status: '', priority: '' };
  statuses = ['Draft', 'Issued', 'In Progress', 'Completed', 'Cancelled'];
  priorities = ['Low', 'Normal', 'Urgent'];

  ngOnInit() {
    this.loadWorkOrders();
  }

  loadWorkOrders() {
    this.isLoading.set(true);
    this.api.getWorkOrders(this.filters).subscribe({
      next: (res: any) => {
        this.workOrders.set(res.data?.work_orders || []);
        this.isLoading.set(false);
      },
      error: () => this.isLoading.set(false)
    });
  }

  summaryCards() {
    const orders = this.workOrders();
    return [
      { label: 'Open', value: orders.filter(wo => ['Draft', 'Issued', 'In Progress'].includes(wo.status)).length, help: 'Active work instructions' },
      { label: 'Urgent', value: orders.filter(wo => wo.priority === 'Urgent').length, help: 'Needs fast attention' },
      { label: 'Completed', value: orders.filter(wo => wo.status === 'Completed').length, help: 'Finished work orders' },
      { label: 'Recipients', value: new Set(orders.map(wo => wo.to_name)).size, help: 'In the current view' }
    ];
  }

  priorityBadgeClass(priority: string): string {
    if (priority === 'Urgent') return 'text-amber-700 bg-amber-50 border-amber-100';
    if (priority === 'Low') return 'text-slate-600 bg-slate-50 border-slate-200';
    return 'text-sky-700 bg-sky-50 border-sky-100';
  }

  statusBadgeClass(status: string): string {
    if (status === 'Completed') return 'text-emerald-700 bg-emerald-50 border-emerald-100';
    if (status === 'Issued' || status === 'In Progress') return 'text-sky-700 bg-sky-50 border-sky-100';
    if (status === 'Cancelled') return 'text-rose-700 bg-rose-50 border-rose-100';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  }
}
